<script setup lang="ts">
import type { LocationQuery } from 'vue-router'
import type { AxiosError } from 'axios'
import type { SubjectCode } from '~/models/Subject'
import type { ReportFilters } from '~/models/ReportFilters'
import type { ReportContentVisit } from '~/models/Report'
import type { GroupMember } from '~/models/Group'
import type { BaseItem } from '~/models/Content/BaseItem'
import { useI18n } from 'vue-i18n'
import { computed, ref, shallowRef, useTemplateRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useDebounceFn, useLocalStorage } from '@vueuse/core'
import { useQuery } from '@tanstack/vue-query'
import { useRoute, useRouter } from '#imports'
import useGroupsStore from '~/stores/groups'
import { useAuthStore } from '~/stores/auth'
import { UserRole } from '~/models/User/UserRole'
import { ColorTheme } from '~/models/Content/Color'
import { useOverlayOutlet } from '~/composables/useOverlayOutlet'
import { useColorTheme } from '~/composables/useColorTheme'
import { useQueryRetry } from '~/composables/queries/useQueryRetry'
import { useActivitiesContentQuery } from '~/composables/queries/useActivitiesContentQuery'
import useReportsApi from '~/api/reportsApi'
import ReportSidebar from '~/components/report/ReportSidebar.vue'
import ReportContentOpenView from '~/components/report/ReportContentOpenView.vue'
import ReportContent from '~/components/report/ReportContent.vue'

const filters = ref<ReportFilters>({
  selectedGroup: undefined,
  selectedSubjects: undefined,
  selectedPeriod: 'last30',
  limit: 10,
  offset: 0,
  ascending: false,
})

const isInitializing = ref(true)

const selectedContent = ref<BaseItem | undefined>()
const selectedVisit = ref<ReportContentVisit | undefined>()

const showActivityDetail = ref(false)
const activeSection = ref<'main' | 'task' | 'assignment'>('main')
const sectionIndex = ref<number | undefined>()
const selectedUserId = ref<string | undefined>()

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const { getReport } = useReportsApi()
const { retry } = useQueryRetry()
const { style } = useColorTheme(ColorTheme.BlueOrange)

const overlayOutlet = useOverlayOutlet(useTemplateRef('overlay-outlet'))

const authStore = useAuthStore()
const { userSubjectsByGrades } = storeToRefs(authStore)

const groupsStore = useGroupsStore()
const { findGroupMembers, findGroupMembersById, loadGroups, findGroupById, loadGroupMembersById } = groupsStore
const { groupMembers: reactiveGroupMembers } = storeToRefs(groupsStore)

const debouncedRouterUpdate = useDebounceFn((query: LocationQuery) => {
  router.replace({ query })
}, 50)

const { data, isLoading, error } = useQuery({
  queryKey: computed(() => ['report', filters.value.selectedGroup?.groupId, filters.value.selectedSubjects, filters.value.selectedPeriod, filters.value.limit, filters.value.offset, filters.value.ascending]),
  queryFn: async () => {
    return getReport(filters.value)
  },
  enabled: computed(() => !!filters.value.selectedGroup?.groupId),
  staleTime: Infinity,
  retry
})

interface FilterParams {
  group?: string
  subjects?: string
  period?: string
}

const storedFilters = useLocalStorage<FilterParams>('report.filters', {})

function pickFirst(queryValue: unknown): string | undefined {
  const v = Array.isArray(queryValue) ? queryValue[0] : queryValue
  if (typeof v === 'string' && v.length > 0) {
    return v
  }
  return undefined
}

function encodeFiltersToParams(): FilterParams {
  return {
    group: filters.value.selectedGroup?.groupId,
    subjects: filters.value.selectedSubjects?.join(','),
    period: filters.value.selectedPeriod,
  }
}

function paramsFromUrl(): FilterParams {
  return {
    group: pickFirst(route.query.group),
    subjects: pickFirst(route.query.subjects),
    period: pickFirst(route.query.period),
  }
}

function areParamsEqual(a: FilterParams, b: FilterParams): boolean {
  return a.group === b.group
    && a.subjects === b.subjects
    && a.period === b.period
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

async function ensureGroupsLoadedAndFind(groupId: string) {
  let group = findGroupById(groupId)
  if (group) {
    return group
  }
  try {
    await loadGroups()
  } catch {
  }
  group = findGroupById(groupId)
  if (group) {
    return group
  }
  for (let i = 0; i < 25; i++) {
    await sleep(200)
    group = findGroupById(groupId)
    if (group) {
      return group
    }
  }
  return undefined
}

const membersLoadInFlight = new Map<string, Promise<void>>()
async function ensureGroupMembersLoaded(groupId: string): Promise<void> {
  if (!groupId) {
    return
  }
  if (findGroupMembersById(groupId).length > 0) {
    return
  }
  const existing = membersLoadInFlight.get(groupId)
  if (existing) {
    await existing
    return
  }
  const loadPromise = (async () => {
    try {
      await loadGroupMembersById(groupId)
    } finally {
      membersLoadInFlight.delete(groupId)
    }
  })()
  membersLoadInFlight.set(groupId, loadPromise)
  await loadPromise
}

async function applyParams(params: FilterParams): Promise<void> {
  let nextGroup
  if (params.group) {
    nextGroup = await ensureGroupsLoadedAndFind(params.group)
  }
  filters.value.selectedGroup = nextGroup

  if (params.subjects) {
    filters.value.selectedSubjects = params.subjects.split(',') as SubjectCode[]
  }
  if (params.period) {
    filters.value.selectedPeriod = params.period
  }
}

(async () => {
  const urlParams = paramsFromUrl()
  const hasUrl = Object.values(urlParams).some(v => typeof v === 'string' && v.length > 0)
  if (hasUrl) {
    await applyParams(urlParams)
    storedFilters.value = urlParams
  } else if (storedFilters.value) {
    await applyParams(storedFilters.value)
  }

  const hasNoSubjects = !filters.value.selectedSubjects || filters.value.selectedSubjects.length === 0
  if (!hasUrl && !storedFilters.value && hasNoSubjects && userSubjectsByGrades.value.length > 0) {
    filters.value.selectedSubjects = [...userSubjectsByGrades.value]
  }

  isInitializing.value = false
})()

watch(
  () => [
    filters.value.selectedGroup?.groupId,
    filters.value.selectedSubjects,
    filters.value.selectedPeriod,
  ],
  () => {
    const params = encodeFiltersToParams()
    storedFilters.value = params
    const nextQuery: LocationQuery = { ...route.query }
    if (params.group) {
      nextQuery.group = params.group
    } else {
      delete nextQuery.group
    }
    if (params.subjects) {
      nextQuery.subjects = params.subjects
    } else {
      delete nextQuery.subjects
    }
    if (params.period) {
      nextQuery.period = params.period
    } else {
      delete nextQuery.period
    }
    debouncedRouterUpdate(nextQuery)
  },
)

watch(
  () => ({
    group: pickFirst(route.query.group),
    subjects: pickFirst(route.query.subjects),
    period: pickFirst(route.query.period),
  }),
  async (q) => {
    const current = encodeFiltersToParams()
    if (!areParamsEqual(current, q)) {
      await applyParams(q)
      storedFilters.value = q
    }
  }
)

watch(() => filters.value.selectedGroup?.groupId, async (groupId) => {
  if (!groupId) {
    return
  }
  await ensureGroupMembersLoaded(groupId)
  updateSelectedGroupMembers()
})

const hasStudents = computed(() => {
  const group = filters.value.selectedGroup
  if (!group) {
    return false
  }
  const members = findGroupMembers(group)
  return members.some(member => member.role !== UserRole.Teacher)
})

const selectedGroupMembers = shallowRef<GroupMember[]>([])

const selectedStudents = computed<GroupMember[]>(() => {
  return selectedGroupMembers.value.filter(member => member.role !== UserRole.Teacher)
})

function updateSelectedGroupMembers() {
  const group = filters.value.selectedGroup
  if (!group) {
    selectedGroupMembers.value = []
    return
  }
  selectedGroupMembers.value = findGroupMembers(group) ?? []
}

watch(() => filters.value.selectedGroup, updateSelectedGroupMembers, { immediate: true })

watch(reactiveGroupMembers, updateSelectedGroupMembers, { deep: true })

const deepContentId = computed<number | undefined>(() => {
  const raw = route.query.contentId
  const str = Array.isArray(raw) ? raw[0] : raw
  const id = Number(str)
  if (Number.isFinite(id) && id > 0) {
    return id
  }
  return undefined
})

const deepSection = computed<'main' | 'task' | 'assignment' | undefined>(() => {
  const raw = route.query.section
  const str = Array.isArray(raw) ? raw[0] : raw
  if (str === 'main' || str === 'task' || str === 'assignment') {
    return str
  }
  return undefined
})

const deepSectionIndex = computed<number | undefined>(() => {
  const raw = route.query.sectionIndex
  const str = Array.isArray(raw) ? raw[0] : raw
  const id = Number(str)
  if (Number.isFinite(id) && id >= 0) {
    return id
  }
  return undefined
})

const deepUserId = computed<string | undefined>(() => {
  const raw = route.query.userId
  const str = Array.isArray(raw) ? raw[0] : raw
  if (typeof str === 'string' && str.length > 0) {
    return str
  }
  return undefined
})

const deepActivities = computed(() => {
  if (!deepContentId.value) {
    return []
  }
  return [{ contentId: deepContentId.value }]
})

const { data: deepContentData } = useActivitiesContentQuery(deepActivities)

watch(deepContentData, (items) => {
  if (!deepContentId.value) {
    return
  }
  const first = items?.[0]
  if (first) {
    selectedContent.value = first
    selectedVisit.value = undefined

    if (deepSection.value) {
      activeSection.value = deepSection.value
      sectionIndex.value = deepSectionIndex.value
      selectedUserId.value = deepUserId.value
      showActivityDetail.value = true
    }
  }
}, { immediate: true })

watch([deepContentId, deepSection, deepUserId], ([contentId, section, userId]) => {
  if (!contentId && selectedContent.value) {
    selectedContent.value = undefined
    selectedVisit.value = undefined
    showActivityDetail.value = false
    selectedUserId.value = undefined
    return
  }

  if (contentId && !section && showActivityDetail.value) {
    showActivityDetail.value = false
    selectedUserId.value = undefined
    return
  }

  if (contentId && section && !userId && selectedUserId.value) {
    selectedUserId.value = undefined
  }
})

function handleOpen(payload: { content: BaseItem, visit: ReportContentVisit | undefined }) {
  selectedContent.value = payload.content
  selectedVisit.value = payload.visit
  const query = { ...route.query, contentId: String(payload.content.contentId) }
  router.push({ query })
}

function handlePageChange(_page: number, offset: number) {
  filters.value.offset = offset
}

function handleSortChange(ascending: boolean) {
  filters.value.ascending = ascending
}

function handleClose() {
  selectedContent.value = undefined
  selectedVisit.value = undefined
  showActivityDetail.value = false
  selectedUserId.value = undefined
  const { contentId, section, sectionIndex, userId, ...rest } = route.query
  debouncedRouterUpdate({ ...rest })
}

function handleOpenDetail(section: 'main' | 'task' | 'assignment', index?: number, userId?: string) {
  activeSection.value = section
  sectionIndex.value = index
  selectedUserId.value = userId
  showActivityDetail.value = true

  const query: LocationQuery = { ...route.query, section }
  if (index !== undefined) {
    query.sectionIndex = String(index)
  } else {
    delete query.sectionIndex
  }
  if (userId) {
    query.userId = userId
  } else {
    delete query.userId
  }
  router.push({ query })
}

function handleCloseDetail() {
  showActivityDetail.value = false
  selectedUserId.value = undefined

  const { section, sectionIndex, userId, ...rest } = route.query
  debouncedRouterUpdate({ ...rest })
}

function handleBreadcrumbMain(): void {
  handleClose()
}

function handleBreadcrumbActivity(): void {
  if (showActivityDetail.value) {
    showActivityDetail.value = false
    selectedUserId.value = undefined
    const { section, sectionIndex, userId, ...rest } = route.query
    debouncedRouterUpdate({ ...rest })
    return
  }
  if (!selectedContent.value || !selectedContent.value) {
    return
  }
  const query = { ...route.query, contentId: String(selectedContent.value.contentId) }
  debouncedRouterUpdate(query)
}

const activityName = computed((): string => {
  if (!selectedContent.value) {
    return t('reports.breadcrumb.activityFallback')
  }
  return selectedContent.value.title || t('reports.breadcrumb.activityFallback')
})

const sectionName = computed((): string => {
  let baseName = ''
  if (activeSection.value === 'main') {
    baseName = t('reports.tabs.activities')
  } else if (activeSection.value === 'task') {
    baseName = `${t('reports.types.task')} ${(sectionIndex.value ?? 0) + 1}`
  } else if (activeSection.value === 'assignment') {
    baseName = `${t('reports.types.assignment')} ${(sectionIndex.value ?? 0) + 1}`
  }

  if (selectedUserId.value && selectedStudents.value.length > 0) {
    const member = selectedStudents.value.find((m: GroupMember) => m.userId === selectedUserId.value)
    const userName = member?.fullName || `${member?.firstName || ''} ${member?.lastName || ''}`.trim() || 'Unknown User'
    return `${baseName} - ${userName}`
  }

  return baseName
})

const errorMessage = computed((): string => {
  if (!error.value) {
    return ''
  }

  const axiosError = error.value as AxiosError
  if (axiosError.response?.status === 404) {
    return t('reports.errors.notFound')
  }

  return t('reports.errors.notWorking')
})
</script>

<template>
  <div>
    <div>
      <div class="bg-green-40 w-full brightness-[1.1]">
        <div class="max-w-screen-au mx-auto px-4 sm:px-8">
          <nav :aria-label="t('reports.breadcrumb.aria')" class="py-3 text-sm sm:text-base">
            <ol class="scrollbar-hide flex min-w-0 items-center gap-1 overflow-x-auto px-2 sm:gap-2 sm:px-4">
              <li class="flex-shrink-0">
                <button
                  v-if="selectedContent"
                  type="button"
                  class="cursor-pointer text-xs whitespace-nowrap text-white underline transition-colors hover:text-white/80 focus-visible:ring sm:text-sm"
                  @click="handleBreadcrumbMain"
                >
                  {{ t('reports.breadcrumb.main') }}
                </button>
                <span
                  v-else
                  class="text-xs font-medium whitespace-nowrap text-white/90 sm:text-sm"
                >
                  {{ t('reports.breadcrumb.main') }}
                </span>
              </li>
              <template v-if="selectedContent">
                <li class="flex-shrink-0 text-xs text-white/60 sm:text-sm">
                  {{ t('reports.breadcrumb.sep') }}
                </li>
                <li class="min-w-0">
                  <button
                    v-if="showActivityDetail"
                    type="button"
                    class="block cursor-pointer truncate text-left text-xs text-white underline transition-colors hover:text-white/80 focus-visible:ring sm:text-sm"
                    style="max-width: 40vw;"
                    :title="activityName"
                    @click="handleBreadcrumbActivity"
                  >
                    {{ activityName }}
                  </button>
                  <span
                    v-else
                    class="block text-left text-xs font-medium text-white/90 sm:text-sm"
                    :title="activityName"
                  >
                    {{ activityName }}
                  </span>
                </li>
              </template>
              <template v-if="selectedContent && showActivityDetail">
                <li class="flex-shrink-0 text-xs text-white/60 sm:text-sm">
                  {{ t('reports.breadcrumb.sep') }}
                </li>
                <li class="min-w-0 flex-shrink-0">
                  <span
                    class="block truncate text-left text-xs font-medium text-white/90 sm:text-sm"
                    style="max-width: 30vw;"
                    :title="sectionName"
                  >
                    {{ sectionName }}
                  </span>
                </li>
              </template>
            </ol>
          </nav>
        </div>
      </div>
      <div class="max-w-screen-au mx-auto mt-4 p-4 px-4 sm:px-8">
        <template v-if="!selectedContent">
          <h2 class="text-green-40 mb-6 px-4 text-2xl font-bold">
            {{ t('reports.activities') }}
          </h2>
          <div class="flex flex-col gap-4 lg:grid lg:grid-cols-[18rem_1fr] lg:gap-6 xl:grid-cols-[20rem_1fr]">
            <ReportSidebar v-model:filters="filters" :is-initializing="isInitializing" />
            <div v-if="!filters.selectedGroup && !isInitializing" class="flex min-w-0 items-center justify-center py-8">
              <div class="text-center">
                <h3 class="mb-2 text-lg font-semibold">
                  {{ t('report.messages.missingGroup.title') }}
                </h3>
                <p>{{ t('report.messages.missingGroup.message') }}</p>
              </div>
            </div>
            <div v-else-if="hasStudents" class="min-w-0">
              <div v-if="errorMessage" class="flex items-center justify-center py-8">
                <p v-text="errorMessage" />
              </div>
              <ReportContent
                v-else
                :filters="filters"
                :report-data="data"
                :is-loading="isLoading"
                :student-count="selectedStudents.length"
                @open="handleOpen"
                @page-change="handlePageChange"
                @sort-change="handleSortChange"
              />
            </div>
          </div>
        </template>
        <template v-else>
          <div class="min-w-0">
            <ReportContentOpenView
              :content="selectedContent"
              :visit="selectedVisit"
              :group-members="selectedStudents"
              :show-activity-detail="showActivityDetail"
              :active-section="activeSection"
              :section-index="sectionIndex"
              :selected-user-id="selectedUserId"
              :filters="filters"
              @close="handleClose"
              @open-detail="handleOpenDetail"
              @close-detail="handleCloseDetail"
            />
          </div>
        </template>
      </div>
    </div>
    <div
      :id="overlayOutlet.id"
      ref="overlay-outlet"
      class="sticky bottom-0 z-[100] bg-(--theme-60) text-(--theme-10) [&:not(:empty)]:p-4"
      :style
    />
  </div>
</template>
